'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Logo } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { NeonLine } from '@/components/public/branding'
import {
  ArrowLeft,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Mail,
  MessageSquare,
  HelpCircle,
  Bug,
  Lightbulb,
  CreditCard,
  AlertTriangle,
} from 'lucide-react'
import { useTranslations } from 'next-intl'

const SUBJECT_KEYS = [
  { value: 'General Inquiry', key: 'subjectGeneralInquiry', icon: HelpCircle },
  { value: 'Technical Issue', key: 'subjectTechnicalIssue', icon: Bug },
  { value: 'Feature Request', key: 'subjectFeatureRequest', icon: Lightbulb },
  { value: 'Billing Question', key: 'subjectBillingQuestion', icon: CreditCard },
  { value: 'Report an Issue', key: 'subjectReportAnIssue', icon: AlertTriangle },
  { value: 'Other', key: 'subjectOther', icon: MessageSquare },
] as const

type FormState = 'idle' | 'submitting' | 'success' | 'error'

export default function ContactPage() {
  const t = useTranslations('contact')
  const [formState, setFormState] = useState<FormState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  // Timestamp when the form was loaded (for timing validation)
  const [loadTimestamp] = useState(() => Date.now())

  // Reset success state after showing for a while
  useEffect(() => {
    if (formState === 'success') {
      const timer = setTimeout(() => {
        setFormState('idle')
        setFormData({ name: '', email: '', subject: '', message: '' })
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [formState])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFormState('submitting')
    setErrorMessage('')

    // Get honeypot value from the hidden field
    const form = e.currentTarget
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="website"]')
    const honeypotValue = honeypotInput?.value || ''

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          website: honeypotValue, // honeypot
          _timestamp: loadTimestamp, // timing validation
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message')
      }

      setFormState('success')
    } catch (error) {
      setFormState('error')
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <Link href="/" className="flex items-center">
              <Logo size="md" />
            </Link>
            <Link href="/">
              <Button variant="ghost" className="text-zinc-400 hover:text-white">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToHome')}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/20 border border-violet-500/40 mb-6">
            <Mail className="h-4 w-4 text-violet-400" />
            <span className="text-sm text-violet-300">{t('getInTouch')}</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('contactUs')}</h1>
          <p className="text-zinc-400 text-lg">
            {t('subtitle')}
          </p>
        </div>

        {formState === 'success' ? (
          <Card className="border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">{t('messageSent')}</h2>
              <p className="text-zinc-400 mb-6">
                {t('messageSentDescription')}
              </p>
              <Button
                variant="outline"
                className="border-zinc-700"
                onClick={() => {
                  setFormState('idle')
                  setFormData({ name: '', email: '', subject: '', message: '' })
                }}
              >
                {t('sendAnotherMessage')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-zinc-800 bg-zinc-900/50">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users, bots will fill it */}
                <div className="absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website">Leave this field empty</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-300">
                      {t('nameLabel')} <span className="text-pink-400">{t('required')}</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder={t('namePlaceholder')}
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      minLength={2}
                      disabled={formState === 'submitting'}
                      className="bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-300">
                      {t('emailLabel')} <span className="text-pink-400">{t('required')}</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      disabled={formState === 'submitting'}
                      className="bg-zinc-800/50 border-zinc-700 focus:border-violet-500"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-zinc-300">
                    {t('subjectLabel')} <span className="text-pink-400">{t('required')}</span>
                  </Label>
                  <Select
                    value={formData.subject}
                    onValueChange={(value) => handleInputChange('subject', value)}
                    disabled={formState === 'submitting'}
                    required
                  >
                    <SelectTrigger className="w-full bg-zinc-800/50 border-zinc-700 focus:border-violet-500">
                      <SelectValue placeholder={t('subjectPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECT_KEYS.map(({ value, key, icon: Icon }) => (
                        <SelectItem key={value} value={value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-zinc-400" />
                            <span>{t(key)}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-zinc-300">
                    {t('messageLabel')} <span className="text-pink-400">{t('required')}</span>
                  </Label>
                  <Textarea
                    id="message"
                    placeholder={t('messagePlaceholder')}
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={6}
                    disabled={formState === 'submitting'}
                    className="bg-zinc-800/50 border-zinc-700 focus:border-violet-500 resize-none"
                  />
                  <p className="text-xs text-zinc-500 text-right">
                    {formData.message.length} / 5000
                  </p>
                </div>

                {/* Error message */}
                {formState === 'error' && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <AlertCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-300 font-medium">{t('failedToSendMessage')}</p>
                      <p className="text-red-400/80 text-sm">{errorMessage}</p>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={formState === 'submitting' || !formData.subject}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)] transition-all"
                >
                  {formState === 'submitting' ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      {t('sendMessage')}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Additional info */}
        <div className="mt-12 text-center">
          <NeonLine color="mixed" />
          <p className="text-zinc-500 text-sm mt-8">
            {t('artistSupport')}{' '}
            <Link href="/login" className="text-violet-400 hover:text-violet-300 transition-colors">
              {t('artistSupportSignIn')}
            </Link>{' '}
            {t('artistSupportSuffix')}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4 mt-auto">
        <div className="container mx-auto max-w-4xl flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <Link href="/terms" className="hover:text-zinc-300 transition-colors">{t('terms')}</Link>
            <Link href="/privacy" className="hover:text-zinc-300 transition-colors">{t('privacy')}</Link>
            <Link href="/contact" className="text-pink-400">{t('contactLink')}</Link>
            <span>{t('copyright', { year: new Date().getFullYear() })}</span>
          </div>
        </div>
      </footer>
    </div>
  )
}








